<template>
  <!-- Registration wrapper -->
  <v-layout
    align-center
    justify-center
    role="region"
    aria-label="Registration area"
    class="register"
  >
    <v-flex
      xs11
      sm11
      md11
      md4
      lg4
    >
      <!-- Main registration card -->
      <v-card color="secondary">
        <!-- Header -->
        <v-app-bar flat>
          <v-toolbar-title
            class="text-body-2"
            role="heading"
            aria-level="1"
          >
            {{ $t('components.register.title') }}
          </v-toolbar-title>

          <v-spacer />

          <!-- “Log in” shortcut -->
          <v-tooltip bottom>
            <template #activator="{ on, attrs }">
              <v-btn
                small
                color="accent"
                :aria-label="$t('components.register.buttons.login')"
                :to="toSignInLink"
                v-bind="attrs"
                v-on="on"
              >
                {{ $t('components.register.buttons.login') }}
              </v-btn>
            </template>
            <span>{{ $t('components.resetPass.buttons.login') }}</span>
          </v-tooltip>
        </v-app-bar>

        <!-- Body -->
        <v-container
          fluid
          grid-list-md
        >
          <!-- Loading indicator -->
          <v-progress-linear
            v-show="isLoading"
            color="accent"
            indeterminate
            role="status"
            aria-label="Submitting your registration, please wait"
          />

          <!-- Consolidated validation / server-side errors -->
          <div
            v-for="(errorsGroup, idx) in filteredErrors"
            :key="idx"
            aria-live="assertive"
            role="status"
          >
            <v-alert
              v-for="error in errorsGroup"
              :key="error.id"
              :type="error.type"
              :icon="error.icon"
              role="alert"
            >
              {{ error.msg }}
            </v-alert>
          </div>

          <!-- Social sign-up options -->
          <v-layout
            justify-center
            align-center
            role="group"
            aria-label="Social sign-up options"
          >
            <v-flex
              text-center
              flex
              xs12
              sm8
              md9
            >
              <v-btn
                icon
                color="accent"
                :href="oAuthLinks.fb"
                aria-label="Sign up with Facebook"
              >
                <v-icon small>
                  fab fa-facebook-f
                </v-icon>
              </v-btn>

              <v-btn
                icon
                color="accent"
                :href="oAuthLinks.google"
                aria-label="Sign up with Google"
              >
                <v-icon small>
                  fab fa-google
                </v-icon>
              </v-btn>

              <v-btn
                icon
                color="accent"
                :href="oAuthLinks.github"
                aria-label="Sign up with GitHub"
              >
                <v-icon small>
                  fab fa-github-alt
                </v-icon>
              </v-btn>
            </v-flex>
          </v-layout>

          <!-- Desktop-only download menu -->
          <v-layout
            justify-center
            align-center
          >
            <v-flex
              text-center
              flex
              xs12
              sm8
              md9
              mb-5
            >
              <v-menu
                v-if="downloadGames.length && $vuetify.breakpoint.mdAndUp"
                bottom
                offset-y
                color="secondary"
                role="menu"
                aria-label="Download game clients"
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    color="accent"
                    aria-label="Download clients"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ $t('components.register.buttons.download') }}
                  </v-btn>
                </template>

                <v-list role="menu">
                  <v-list-item
                    v-for="game in downloadGames"
                    :key="game.id"
                    link
                    role="menuitem"
                    @click="goTo(game.link)"
                  >
                    <div
                      v-if="game.useImageAsBackground"
                      class="v-list-item-background-image"
                      :style="`background-image: url('${game.img}')`"
                    >
                      <div class="v-list-item-download-labels">
                        <span
                          v-if="game.isNew"
                          class="downloadLabel downloadNew"
                        >New</span>
                        <span
                          v-if="game.isRecommend"
                          class="downloadLabel downloadRecommend"
                        >Recommend</span>
                        <span
                          v-if="game.isRecommend"
                          class="downloadLabel downloadComingSoon"
                        >Coming Soon</span>
                      </div>
                    </div>

                    <template v-else>
                      <v-list-item-avatar size="25">
                        <v-icon>{{ game.icon }}</v-icon>
                      </v-list-item-avatar>
                      <v-list-item-title class="text-no-wrap">
                        {{ $t(game.title) }}
                        <div class="v-list-item-download-labels">
                          <span
                            v-if="game.isNew"
                            class="downloadLabel downloadNew"
                          >New</span>
                          <span
                            v-if="game.isRecommend"
                            class="downloadLabel downloadRecommend"
                          >Recommend</span>
                          <span
                            v-if="game.isComingSoon"
                            class="downloadLabel downloadComingSoon"
                          >Coming Soon</span>
                        </div>
                      </v-list-item-title>
                    </template>
                  </v-list-item>
                </v-list>
              </v-menu>
            </v-flex>
          </v-layout>

          <!-- Community / promo banners -->
          <v-alert
            :value="discordCommunity.enabled"
            :type="discordCommunity.type"
            :icon="discordCommunity.icon"
            class="alert-discord"
            aria-label="Join our Discord community"
            @click="onClickDiscordLink"
          >
            {{ $t('components.register.communities.discord') }}
          </v-alert>

          <v-alert
            v-if="newbieGift.link"
            :type="newbieGift.type"
            :icon="newbieGift.icon"
            class="alert-discord"
            :aria-label="$t(newbieGift.name)"
            @click="onClickGiftsLink"
          >
            {{ $t(newbieGift.name) }}
          </v-alert>

          <v-alert
            v-if="getReferredFromUser"
            type="info"
            icon="fad fa-sun"
            class="alert-referral"
            role="status"
            aria-live="polite"
          >
            {{ $t('components.register.invited', { nickname: getReferredFromUser }) }}
          </v-alert>

          <!-- Registration form -->
          <v-form
            ref="form"
            role="form"
            aria-label="Registration form"
            @submit.prevent="validate"
          >
            <!-- Username -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.username"
                  v-validate="'required|min:6|max:16'"
                  prepend-icon="fal fa-user"
                  color="accent"
                  data-vv-name="username"
                  :label="$t('components.register.form.username') + ' *'"
                  :error-messages="errors.collect('username')"
                  name="username"
                  autocomplete="username"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- Email -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.email"
                  v-validate="'required|email|min:6|max:255'"
                  prepend-icon="fal fa-envelope"
                  color="accent"
                  data-vv-name="email"
                  :label="$t('components.register.form.email') + ' *'"
                  :error-messages="errors.collect('email')"
                  name="email"
                  type="email"
                  autocomplete="email"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- Nickname -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.nickname"
                  v-validate="'required|min:6|max:80'"
                  prepend-icon="fal fa-user-crown"
                  color="accent"
                  data-vv-name="nickname"
                  :label="$t('components.register.form.nickname') + ' *'"
                  :error-messages="errors.collect('nickname')"
                  name="nickname"
                  autocomplete="nickname"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- Password -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  ref="password"
                  v-model="form.password"
                  v-validate="'required|min:6|max:50'"
                  prepend-icon="fal fa-key"
                  color="accent"
                  data-vv-name="password"
                  :label="$t('components.register.form.pass') + ' *'"
                  :error-messages="errors.collect('password')"
                  name="password"
                  type="password"
                  autocomplete="new-password"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- Confirm password -->
            <v-layout>
              <v-flex xs12>
                <v-text-field
                  v-model="form.confirmPassword"
                  v-validate="'required|min:6|max:50|confirmed:password'"
                  prepend-icon="fal fa-key"
                  color="accent"
                  data-vv-name="confirmPassword"
                  :label="$t('components.register.form.confirmPass') + ' *'"
                  :error-messages="errors.collect('confirmPassword')"
                  name="confirmPassword"
                  type="password"
                  autocomplete="new-password"
                  aria-required="true"
                />
              </v-flex>
            </v-layout>

            <!-- Terms & Policy switches -->
            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="form.acceptedTerms"
                  color="accent"
                  :aria-checked="form.acceptedTerms"
                  :aria-label="$t('components.register.form.acceptTerms')"
                >
                  <template #label>
                    <div>
                      {{ $t('components.register.form.acceptTerms') }}
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <a
                            target="_blank"
                            :href="toTCLink"
                            class="register-important-link"
                            :aria-label="$t('components.register.form.terms')"
                            @click.stop
                            v-on="on"
                          >
                            {{ $t('components.register.form.terms') }}
                          </a>
                        </template>
                        {{ $t('components.register.form.acceptTermsDialog') }}
                      </v-tooltip>
                    </div>
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="form.acceptMarketingNews"
                  color="accent"
                  flat
                >
                  <template #label>
                    <div>
                      {{ $t('components.register.form.acceptMarketingNews') }}
                    </div>
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <v-layout>
              <v-flex xs12>
                <v-switch
                  v-model="form.acceptedPolicy"
                  color="accent"
                  :aria-checked="form.acceptedPolicy"
                  :aria-label="$t('components.register.form.acceptPolicy')"
                >
                  <template #label>
                    <div>
                      {{ $t('components.register.form.acceptPolicy') }}
                      <v-tooltip bottom>
                        <template #activator="{ on }">
                          <a
                            target="_blank"
                            :href="toPolicyLink"
                            class="register-important-link"
                            :aria-label="$t('components.register.form.policy')"
                            @click.stop
                            v-on="on"
                          >
                            {{ $t('components.register.form.policy') }}
                          </a>
                        </template>
                        {{ $t('components.register.form.acceptPolicyDialog') }}
                      </v-tooltip>
                    </div>
                  </template>
                </v-switch>
              </v-flex>
            </v-layout>

            <!-- Form actions -->
            <v-card-actions>
              <v-btn
                small
                color="transparent"
                class="register-forgot-password-link"
                :to="toForgotPassLink"
                :aria-label="$t('components.register.buttons.resetPass')"
              >
                {{ $t('components.register.buttons.resetPass') }}
              </v-btn>

              <v-spacer />

              <v-btn
                color="accent"
                type="submit"
                :aria-label="$t('components.register.buttons.register')"
                :disabled="!(form.acceptedTerms && form.acceptedPolicy)"
              >
                {{ $t('components.register.buttons.register') }}
              </v-btn>
            </v-card-actions>
          </v-form>

          <!-- Mobile “download clients” bottom-sheet -->
          <v-bottom-sheet v-model="isSheetOpen">
            <v-list>
              <v-subheader>{{ $t('components.rapidSignUp.download.header') }}</v-subheader>
              <v-list-item
                v-for="game in downloadGames"
                :key="game.id"
                link
                @click="goTo(game.link)"
              >
                <template v-if="game.useImageAsBackground">
                  <div
                    class="v-list-item-background-image"
                    :style="`background-image:url('${game.img}')`"
                  >
                    <div class="v-list-item-download-labels">
                      <span
                        v-if="game.isNew"
                        class="downloadLabel downloadNew"
                      >New</span>
                      <span
                        v-if="game.isRecommend"
                        class="downloadLabel downloadRecommend"
                      >Recommend</span>
                      <span
                        v-if="game.isComingSoon"
                        class="downloadLabel downloadComingSoon"
                      >Coming Soon</span>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <v-list-item-avatar size="25">
                    <v-icon>{{ game.icon }}</v-icon>
                  </v-list-item-avatar>
                  <v-list-item-title class="text-no-wrap">
                    {{ $t(game.title) }}
                    <div class="v-list-item-download-labels">
                      <span
                        v-if="game.isNew"
                        class="downloadLabel downloadNew"
                      >New</span>
                      <span
                        v-if="game.isRecommend"
                        class="downloadLabel downloadRecommend"
                      >Recommend</span>
                      <span
                        v-if="game.isComingSoon"
                        class="downloadLabel downloadComingSoon"
                      >Coming Soon</span>
                    </div>
                  </v-list-item-title>
                </template>
              </v-list-item>
            </v-list>
          </v-bottom-sheet>
        </v-container>
      </v-card>
    </v-flex>
  </v-layout>
</template>

<script>
import { mapGetters } from 'vuex';
import { ValidationProvider } from "vee-validate";
import downloadGames from "@/constants/download.games";
import communities from "@/constants/communities.js";
import { filterByKeys } from "@/utils/object.utils";
import { CookieRemove, CookieSet } from "@/utils/cookie.utils";
import blacklist from "@/constants/blacklist";
import links from "@/constants/links";
import Validator from "@/modules/validator.module";

const ValidatorModule = new Validator();

export default {
    name: "SignUpLayout",
    components: {
        // eslint-disable-next-line vue/no-unused-components
        ValidationProvider
    },
    inject: ["$validator"],
    data() {
        const defaultForm = Object.freeze({
            username: "",
            nickname: "",
            email: "",
            password: "",
            confirmPassword: "",
            acceptedPolicy: false,
            acceptedTerms: false,
            acceptMarketingNews: false,
        });

        return {
            form: Object.assign({}, defaultForm),
            downloadGames: downloadGames.list,
            newbieGift: {
                ...links.newbieGift,
                link: links.general.registerGiftLink
            },
            oAuthLinks: links.oAuth,
            isSheetOpen: false,
            isLoading: false,
            defaultForm
        }
    },
    computed: {
        ...mapGetters(['getReferredFromUser', 'isLogged']),

        filteredErrors() {
            return filterByKeys(
                this.$validator.errors.collect(),
                blacklist.forms.register
            );
        },
        toForgotPassLink() {
            return links.routes.resetLink;
        },
        toSignInLink() {
            return links.routes.login;
        },
        toTCLink() {
            return links.routes.tcConditions;
        },
        toPolicyLink() {
            return links.routes.tcPolicy;
        },
        discordCommunity() {
            return {
                enabled: true,
                type: "info",
                ...(communities.list.find((community) => community.id === 2) || {}),
            }
        },
    },
    watch: {
        isLogged(newValue) {
            if (newValue) {
                this.$router.push(links.routes.profileOverview);
            }
        },
        'form.acceptedPolicy'(newValue) {
            if (newValue) {
                ValidatorModule.clear();

                return;
            }

            ValidatorModule.add("acceptedPolicy", {
                type: 0,
                msg: 'Policy is required to be accepted to proceed'
            });
        },
        'form.acceptedTerms'(newValue) {
            if (newValue) {
                ValidatorModule.clear();

                return;
            }

            ValidatorModule.add("acceptedTerms", {
                type: 0,
                msg: 'Terms & Conditions is required to be accepted to proceed'
            });
        },
        isSheetOpen(value) {
            if (this.isCompletedRegistration && !value) {
                this.resetForm();
            }
        }
    },
    mounted() {
        const referralId = this.$route.params.referral;
        if (referralId) {
            CookieSet("referralId", referralId);
            // track event
            // this.$ga.event("referral-link", "id", referralId);
        } else {
            CookieRemove("referralId");
        }
    },
    methods: {
        goTo(link) {
            // track event
            // this.$ga.event("register", "download-game", link);
            // go to link
            window.open(link);
        },
        async validate() {
            if (await this.$validator.validateAll()) {
                if (!this.form.acceptedTerms ||
                !this.form.acceptedPolicy) {
                    return;
                }

                this.isCompletedRegistration = false;
                this.isLoading = true;
                this.isSheetOpen = false;
                this.$api.register(
                        this.form.username,
                        this.form.nickname,
                        this.form.email,
                        this.form.password,
                        this.form.confirmPassword,
                        this.form.acceptMarketingNews,
                    )
                .then(() => {
                    this.isLoading = false;
                    this.isSheetOpen = true;
                    this.isCompletedRegistration = true;
                })
                .catch(() => {
                    this.isLoading = false;
                    this.isSheetOpen = false;
                });
            }
        },
        onClickDiscordLink() {
            // go to link
            window.open(this.discordCommunity.link);
        },
        onClickGiftsLink() {
            // go to link
            window.open(this.newbieGift.link);
        },
        resetForm () {
            this.form = Object.assign({}, this.defaultForm)

            this.$refs.form.reset()
        },
    }
};
</script>


<style lang="scss">
@import '~/_variables.scss';

    .register{
        background-image: url('https://gm.goz.fun/wp-content/uploads/2023/03/register-cover.jpg');
        background-size: cover;
        background-position: center center;

        &-forgot-password-link,
        &-important-link{
            color: $accent!important;

            &:hover{
                color: lighten($accent, 10%)!important;
            }
        }

        .alert-discord,
        .alert-referral {
            text-align: center;
        }
    }
</style>
